// ===============================
// pages/Checkout.jsx
// ===============================

import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { CartContext } from "../../context/CartContext";
import "./Checkout.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const USER_API = `${API_BASE_URL}/api/user`;

const RAZORPAY_SCRIPT_SRC = "https://checkout.razorpay.com/v1/checkout.js";

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

const EMPTY_ADDRESS = {
  fullName: "",
  phone: "",
  address: "",
  locality: "",
  city: "",
  state: "",
  pincode: "",
  landmark: "",
  addressType: "Home",
  isDefault: false,
};

const loadRazorpayScript = () =>
  new Promise((resolve, reject) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }

    const existingScript = document.querySelector(
      `script[src="${RAZORPAY_SCRIPT_SRC}"]`
    );

    if (existingScript) {
      existingScript.addEventListener("load", () => resolve(true));
      existingScript.addEventListener("error", () =>
        reject(new Error("Failed to load Razorpay"))
      );
      return;
    }

    const script = document.createElement("script");
    script.src = RAZORPAY_SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => reject(new Error("Failed to load Razorpay"));
    document.body.appendChild(script);
  });

const getImageUrl = (src) => {
  if (!src) return "/placeholder-product.png";

  if (
    src.startsWith("http://") ||
    src.startsWith("https://") ||
    src.startsWith("blob:") ||
    src.startsWith("data:")
  ) {
    return src;
  }

  const cleanBase = API_BASE_URL.replace(/\/$/, "");
  const cleanPath = src.startsWith("/") ? src : `/${src}`;
  return `${cleanBase}${cleanPath}`;
};

function Checkout() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const storedUser = JSON.parse(localStorage.getItem("user") || "null");

  const {
    couponCode = "",
    couponApplied = false,
    discount = 0,
    finalTotal = 0,
    clearCartSilently,
    setCartItems,
    removeCoupon,
  } = useContext(CartContext);

  const [cartItems, setLocalCartItems] = useState([]);
  const [loadingCart, setLoadingCart] = useState(true);
  const [loadingAddresses, setLoadingAddresses] = useState(false);
  const [placingOrder, setPlacingOrder] = useState(false);
  const [error, setError] = useState("");

  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState("");
  const [showNewAddressForm, setShowNewAddressForm] = useState(false);
  const [address, setAddress] = useState(EMPTY_ADDRESS);

  const getAuthConfig = () => ({
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const handleUnauthorized = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/login");
  };

  const normalizeCartItems = (data) => {
    const possibleItems =
      data?.items ||
      data?.cart?.items ||
      data?.data?.items ||
      (Array.isArray(data) ? data : []) ||
      [];

    if (!Array.isArray(possibleItems)) return [];

    return possibleItems
      .map((item) => {
        const product = item.product || item.productId || {};
        const rawPrice = item.price ?? product.price ?? item.productPrice ?? 0;
        const rawQuantity = item.quantity ?? 1;

        return {
          ...item,
          product,
          _id: item._id || product._id || item.productId || null,
          name: item.name || product.name || "Product",
          image:
            item.image ||
            product.image ||
            (Array.isArray(item.images) ? item.images[0] : "") ||
            (Array.isArray(product.images) ? product.images[0] : "") ||
            "",
          price: Number(rawPrice) || 0,
          quantity: Math.max(1, Number(rawQuantity) || 1),
        };
      })
      .filter((item) => item._id);
  };

  const mapAddressFromApi = (item = {}) => ({
    _id: item._id || item.id || "",
    fullName: item.fullName || "",
    phone: item.phone || "",
    address: item.address || "",
    locality: item.locality || "",
    city: item.city || "",
    state: item.state || "",
    pincode: item.pincode || "",
    landmark: item.landmark || "",
    addressType: item.addressType || "Home",
    isDefault: !!item.isDefault,
  });

  const getSanitizedAddressPayload = () => ({
    fullName: address.fullName.trim(),
    phone: address.phone.trim(),
    address: address.address.trim(),
    locality: address.locality.trim(),
    city: address.city.trim(),
    state: address.state.trim(),
    pincode: address.pincode.trim(),
    landmark: address.landmark.trim(),
    addressType: address.addressType || "Home",
    isDefault: !!address.isDefault,
  });

  useEffect(() => {
    if (!token) {
      navigate("/login");
      return;
    }

    let isMounted = true;

    const initializeCheckout = async () => {
      await Promise.all([
        loadCart(isMounted),
        loadSavedAddresses(isMounted),
        loadRazorpayScript().catch(() => null),
      ]);
    };

    initializeCheckout();

    return () => {
      isMounted = false;
    };
  }, [token, navigate]);

  const loadCart = async (isMounted = true) => {
    try {
      if (isMounted) {
        setLoadingCart(true);
        setError("");
      }

      const res = await api.get("/api/cart", getAuthConfig());
      const normalizedItems = normalizeCartItems(res.data);

      if (isMounted) {
        setLocalCartItems(normalizedItems);
      }
    } catch (err) {
      if (err.response?.status === 401) {
        handleUnauthorized();
        return;
      }

      if (isMounted) {
        setLocalCartItems([]);
        setError(err.response?.data?.message || "Failed to load cart.");
      }
    } finally {
      if (isMounted) {
        setLoadingCart(false);
      }
    }
  };

  const loadSavedAddresses = async (isMounted = true) => {
    try {
      if (isMounted) {
        setLoadingAddresses(true);
      }

      const res = await axios.get(`${USER_API}/profile`, getAuthConfig());
      const list = Array.isArray(res.data?.addresses) ? res.data.addresses : [];

      if (!isMounted) return;

      setSavedAddresses(list);

      if (list.length > 0) {
        setShowNewAddressForm(false);

        const defaultAddress = list.find((item) => item.isDefault) || list[0];

        if (defaultAddress) {
          setSelectedAddressId(defaultAddress._id);
          setAddress({
            fullName: defaultAddress.fullName || "",
            phone: defaultAddress.phone || "",
            address: defaultAddress.address || "",
            locality: defaultAddress.locality || "",
            city: defaultAddress.city || "",
            state: defaultAddress.state || "",
            pincode: defaultAddress.pincode || "",
            landmark: defaultAddress.landmark || "",
            addressType: defaultAddress.addressType || "Home",
            isDefault: !!defaultAddress.isDefault,
          });
        }
      } else {
        setShowNewAddressForm(true);
        setAddress(EMPTY_ADDRESS);
      }
    } catch (err) {
      if (err.response?.status === 401) {
        handleUnauthorized();
        return;
      }

      if (isMounted) {
        setSavedAddresses([]);
      }
    } finally {
      if (isMounted) {
        setLoadingAddresses(false);
      }
    }
  };

  const saveNewAddressToDb = async () => {
    const payload = getSanitizedAddressPayload();

    const res = await api.post(`${USER_API}/address`, payload, getAuthConfig());

    const rawAddress =
      res.data?.address ||
      res.data?.savedAddress ||
      res.data?.data?.address ||
      res.data?.data ||
      res.data?.result ||
      null;

    if (!rawAddress) {
      return {
        ...payload,
        _id: "",
      };
    }

    return mapAddressFromApi(rawAddress);
  };

  const handleAddressSelect = (selected) => {
    setSelectedAddressId(selected._id);
    setShowNewAddressForm(false);
    setError("");
    setAddress({
      fullName: selected.fullName || "",
      phone: selected.phone || "",
      address: selected.address || "",
      locality: selected.locality || "",
      city: selected.city || "",
      state: selected.state || "",
      pincode: selected.pincode || "",
      landmark: selected.landmark || "",
      addressType: selected.addressType || "Home",
      isDefault: !!selected.isDefault,
    });
  };

  const handleAddNewAddress = () => {
    setSelectedAddressId("");
    setShowNewAddressForm(true);
    setError("");
    setAddress(EMPTY_ADDRESS);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setError("");
    setAddress((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const subtotal = useMemo(() => {
    return cartItems.reduce((sum, item) => {
      return sum + (Number(item.price) || 0) * (Number(item.quantity) || 1);
    }, 0);
  }, [cartItems]);

  // 🚚 DYNAMIC SHIPPING RULE: Free if subtotal is ₹499 or more, otherwise ₹59
  const shippingFee = useMemo(() => {
    return subtotal >= 499 ? 0 : 59;
  }, [subtotal]);

  const appliedDiscount = useMemo(() => {
    return Number(couponApplied ? discount : 0) || 0;
  }, [couponApplied, discount]);

  const totalAmount = useMemo(() => {
    if (couponApplied && Number(finalTotal) >= 0) {
      return Number(finalTotal) + Number(shippingFee);
    }
    return Math.max(subtotal + shippingFee - appliedDiscount, 0);
  }, [couponApplied, finalTotal, subtotal, shippingFee, appliedDiscount]);

  const validateAddress = () => {
    if (!address.fullName.trim()) return "Full name is required.";
    if (!/^[6-9]\d{9}$/.test(address.phone.trim())) {
      return "Enter a valid 10-digit phone number.";
    }
    if (!address.address.trim()) return "Address is required.";
    if (!address.locality.trim()) return "Locality is required.";
    if (!address.city.trim()) return "City is required.";
    if (!address.state.trim()) return "State is required.";
    if (!/^\d{6}$/.test(address.pincode.trim())) {
      return "Enter a valid 6-digit pincode.";
    }
    return "";
  };

  const buildOrderPayload = () => ({
    userId: storedUser?._id,
    items: cartItems.map((item) => ({
      product:
        item.product?._id ||
        item.product?.id ||
        item.productId?._id ||
        item.productId ||
        item._id,
      name: item.name || item.product?.name || "Product",
      image:
        item.image ||
        item.product?.image ||
        (Array.isArray(item.product?.images) ? item.product.images[0] : "") ||
        (Array.isArray(item.images) ? item.images[0] : "") ||
        "",
      price: Number(item.price) || 0,
      quantity: Number(item.quantity) || 1,
    })),
    shippingAddress: getSanitizedAddressPayload(),
    subtotal,
    shippingFee,
    discount: appliedDiscount,
    totalAmount,
    couponCode: couponApplied ? couponCode : "",
    notes: couponApplied ? `Coupon applied: ${couponCode}` : "",
  });

  const handlePlaceOrder = async () => {
    if (placingOrder) return;

    if (!storedUser?._id) {
      setError("User session not found. Please login again.");
      return;
    }

    if (!cartItems.length) {
      setError("Your cart is empty.");
      return;
    }

    if (showNewAddressForm || savedAddresses.length === 0) {
      const validationError = validateAddress();
      if (validationError) {
        setError(validationError);
        return;
      }
    } else if (!selectedAddressId) {
      setError("Please select a saved address or add a new one.");
      return;
    }

    try {
      setPlacingOrder(true);
      setError("");

      await loadRazorpayScript();

      if (!window.Razorpay) {
        throw new Error("Razorpay SDK not available");
      }

      if (showNewAddressForm || savedAddresses.length === 0) {
        const savedAddress = await saveNewAddressToDb();

        if (savedAddress?._id) {
          setSavedAddresses((prev) => {
            const exists = prev.some((item) => item._id === savedAddress._id);
            return exists ? prev : [savedAddress, ...prev];
          });

          setSelectedAddressId(savedAddress._id);
        }

        setShowNewAddressForm(false);
        setAddress({
          fullName: savedAddress.fullName || "",
          phone: savedAddress.phone || "",
          address: savedAddress.address || "",
          locality: savedAddress.locality || "",
          city: savedAddress.city || "",
          state: savedAddress.state || "",
          pincode: savedAddress.pincode || "",
          landmark: savedAddress.landmark || "",
          addressType: savedAddress.addressType || "Home",
          isDefault: !!savedAddress.isDefault,
        });

        if (!savedAddress?._id) {
          await loadSavedAddresses(true);
        }
      }

      const payload = buildOrderPayload();

      const paymentOrderRes = await api.post(
        "/api/payments/create-order",
        payload,
        getAuthConfig()
      );

      const responseData = paymentOrderRes.data || {};
      const backendOrderId = responseData.orderId;
      const razorpayOrder = responseData.razorpayOrder;
      const razorpayOrderId = razorpayOrder?.id;
      const amount = razorpayOrder?.amount;
      const key = responseData.key;

      if (!backendOrderId || !razorpayOrderId || !amount || !key) {
        throw new Error("Invalid create-order response from server");
      }

      const options = {
        key,
        amount,
        currency: razorpayOrder.currency || "INR",
        name: "Versacore",
        description: "Secure Checkout",
        order_id: razorpayOrderId,
        handler: async function (response) {
          try {
            const verifyRes = await api.post(
              "/api/payments/verify-payment",
              {
                orderId: backendOrderId,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              },
              getAuthConfig()
            );

            if (verifyRes.data?.success) {
              if (typeof clearCartSilently === "function") {
                await clearCartSilently();
              } else {
                setLocalCartItems([]);
                if (typeof setCartItems === "function") setCartItems([]);
                if (typeof removeCoupon === "function") removeCoupon(false);
                try {
                  await api.delete("/api/cart/clear", getAuthConfig());
                } catch (clearError) {}
              }

              sessionStorage.setItem("lastOrderSuccess", backendOrderId);

              navigate(`/order-success/${backendOrderId}`, {
                replace: true,
                state: { 
                  paymentSuccess: true,
                  order: verifyRes.data.order
                },
              });
            } else {
              setError("Payment verification failed.");
            }
          } catch (err) {
            setError(
              err.response?.data?.message ||
                "Payment done, but verification failed."
            );
          } finally {
            setPlacingOrder(false);
          }
        },
        prefill: {
          name: payload.shippingAddress.fullName,
          email: storedUser?.email || "customer@versacore.in", 
          contact: payload.shippingAddress.phone,
        },
        notes: {
          address: `${payload.shippingAddress.address}, ${payload.shippingAddress.city}`.substring(0, 200),
        },
        theme: {
          color: "#9c6b3d", 
        },
        modal: {
          ondismiss: function () {
            setPlacingOrder(false);
            setError("Payment window closed. Please try again.");
          },
        },
      };

      const rzp = new window.Razorpay(options);

      rzp.on("payment.failed", function (response) {
        setPlacingOrder(false);
        setError(
          response?.error?.description || "Payment failed. Please try again."
        );
      });

      rzp.open();
    } catch (err) {
      if (err.response?.status === 401) {
        handleUnauthorized();
        return;
      }

      setPlacingOrder(false);
      setError(
        err.response?.data?.message ||
          err.message ||
          "Failed to initiate payment."
      );
    }
  };

  if (loadingCart) {
    return (
      <div className="checkout-page">
        <div className="checkout-shell">
          <div className="checkout-loading-card">
            <div className="checkout-spinner"></div>
            <h3>Loading checkout...</h3>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-page">
      <div className="checkout-shell">
        <div className="checkout-main">
          <div className="checkout-breadcrumb">
            <Link to="/cart">Cart</Link>
            <span>/</span>
            <span>Checkout</span>
          </div>

          <div className="checkout-form-card">
            <div className="section-head">
              <div>
                <h2>Shipping address</h2>
                <p>Select a saved address or add a new one for this order.</p>
              </div>

              {savedAddresses.length > 0 && (
                <button
                  type="button"
                  className="add-new-address-btn"
                  onClick={handleAddNewAddress}
                  disabled={placingOrder}
                >
                  + Add New Address
                </button>
              )}
            </div>

            {savedAddresses.length > 0 && (
              <div className="saved-address-section">
                <div className="saved-address-header">
                  <h3>Saved addresses</h3>
                  <span>{savedAddresses.length} available</span>
                </div>

                <div className="saved-address-list">
                  {savedAddresses.map((item) => (
                    <label
                      key={item._id}
                      className={`saved-address-card ${
                        selectedAddressId === item._id ? "active" : ""
                      }`}
                    >
                      <input
                        type="radio"
                        name="selectedAddress"
                        className="radio-btn"
                        checked={selectedAddressId === item._id}
                        onChange={() => handleAddressSelect(item)}
                        disabled={placingOrder}
                      />

                      <div className="saved-address-content">
                        <div className="saved-address-top">
                          <h4>{item.fullName}</h4>
                          <div className="saved-address-tags">
                            {item.addressType && (
                              <span className="address-chip">
                                {item.addressType}
                              </span>
                            )}
                            {item.isDefault && (
                              <span className="saved-badge">Default</span>
                            )}
                          </div>
                        </div>

                        <p>{item.address}</p>
                        <p>
                          {item.locality}, {item.city}, {item.state} -{" "}
                          {item.pincode}
                        </p>
                        <p>{item.phone}</p>
                      </div>
                    </label>
                  ))}
                </div>

                {loadingAddresses && (
                  <p className="checkout-muted">Loading saved addresses...</p>
                )}
              </div>
            )}

            {(savedAddresses.length === 0 || showNewAddressForm) && (
              <>
                <div className="manual-entry-note">
                  <span className="manual-entry-dot"></span>
                  {savedAddresses.length > 0
                    ? "Add a new delivery address for this order."
                    : "Enter your delivery details to continue."}
                </div>

                <div className="checkout-grid">
                  <div className="field-group">
                    <label htmlFor="fullName">Full Name</label>
                    <input
                      id="fullName"
                      name="fullName"
                      placeholder="Enter full name"
                      value={address.fullName}
                      onChange={handleChange}
                      disabled={placingOrder}
                    />
                  </div>

                  <div className="field-group">
                    <label htmlFor="phone">Phone Number</label>
                    <input
                      id="phone"
                      name="phone"
                      inputMode="numeric"
                      maxLength={10}
                      placeholder="Enter phone number"
                      value={address.phone}
                      onChange={handleChange}
                      disabled={placingOrder}
                    />
                  </div>

                  <div className="field-group field-span-2">
                    <label htmlFor="address">Address</label>
                    <input
                      id="address"
                      name="address"
                      placeholder="House no, street, building name"
                      value={address.address}
                      onChange={handleChange}
                      disabled={placingOrder}
                    />
                  </div>

                  <div className="field-group">
                    <label htmlFor="locality">Locality</label>
                    <input
                      id="locality"
                      name="locality"
                      placeholder="Area / locality"
                      value={address.locality}
                      onChange={handleChange}
                      disabled={placingOrder}
                    />
                  </div>

                  <div className="field-group">
                    <label htmlFor="city">City</label>
                    <input
                      id="city"
                      name="city"
                      placeholder="Enter city"
                      value={address.city}
                      onChange={handleChange}
                      disabled={placingOrder}
                    />
                  </div>

                  <div className="field-group">
                    <label htmlFor="state">State</label>
                    <input
                      id="state"
                      name="state"
                      placeholder="Enter state"
                      value={address.state}
                      onChange={handleChange}
                      disabled={placingOrder}
                    />
                  </div>

                  <div className="field-group">
                    <label htmlFor="pincode">Pincode</label>
                    <input
                      id="pincode"
                      name="pincode"
                      inputMode="numeric"
                      maxLength={6}
                      placeholder="Enter pincode"
                      value={address.pincode}
                      onChange={handleChange}
                      disabled={placingOrder}
                    />
                  </div>

                  <div className="field-group">
                    <label htmlFor="landmark">Landmark</label>
                    <input
                      id="landmark"
                      name="landmark"
                      placeholder="Nearby landmark (optional)"
                      value={address.landmark}
                      onChange={handleChange}
                      disabled={placingOrder}
                    />
                  </div>

                  <div className="field-group">
                    <label htmlFor="addressType">Address Type</label>
                    <select
                      id="addressType"
                      name="addressType"
                      value={address.addressType}
                      onChange={handleChange}
                      disabled={placingOrder}
                    >
                      <option>Home</option>
                      <option>Office</option>
                      <option>Other</option>
                    </select>
                  </div>

                  <div className="field-group field-span-2">
                    <label className="checkbox-group">
                      <input
                        type="checkbox"
                        name="isDefault"
                        checked={!!address.isDefault}
                        onChange={handleChange}
                        disabled={placingOrder}
                      />
                      Set as Default Address
                    </label>
                  </div>
                </div>
              </>
            )}

            {error && <p className="checkout-error">{error}</p>}
          </div>
        </div>

        <aside className="checkout-summary-card">
          <h2>Order summary</h2>

          <div className="checkout-items">
            {cartItems.length > 0 ? (
              cartItems.map((item, index) => {
                const product = item.product || {};
                const name = item.name || product.name || "Product";
                const image = getImageUrl(
                  item.image ||
                    product.image ||
                    (Array.isArray(product.images) ? product.images[0] : "") ||
                    (Array.isArray(item.images) ? item.images[0] : "")
                );
                const price = Number(item.price) || 0;
                const quantity = Number(item.quantity) || 1;

                return (
                  <div
                    className="checkout-item"
                    key={product._id || item._id || index}
                  >
                    <img
                      src={image}
                      alt={name}
                      onError={(e) => {
                        e.currentTarget.src = "/placeholder-product.png";
                      }}
                    />
                    <div className="checkout-item-info">
                      <h4>{name}</h4>
                      <p>Qty: {quantity}</p>
                    </div>
                    <strong>₹{price * quantity}</strong>
                  </div>
                );
              })
            ) : (
              <p className="checkout-muted">Your cart is empty.</p>
            )}
          </div>

          <div className="summary-breakdown">
            <div>
              <span>Subtotal</span>
              <strong>₹{subtotal}</strong>
            </div>

            <div>
              <span>Shipping</span>
              <strong>
                {shippingFee === 0 ? (
                  <span style={{ color: "#2ecc71" }}>Free</span>
                ) : (
                  `₹${shippingFee}`
                )}
              </strong>
            </div>

            {/* Free Shipping Incentive Notice */}
            {subtotal < 499 && subtotal > 0 && (
              <p style={{ fontSize: "0.82rem", color: "#e67e22", margin: "4px 0 10px" }}>
                Add <strong>₹{499 - subtotal}</strong> more for <strong>Free Shipping!</strong>
              </p>
            )}

            {couponApplied ? (
              <>
                <div>
                  <span>Coupon</span>
                  <strong>{couponCode}</strong>
                </div>
                <div className="discount-row">
                  <span>Discount</span>
                  <strong>- ₹{appliedDiscount}</strong>
                </div>
              </>
            ) : (
              <div>
                <span>Discount</span>
                <strong>₹0</strong>
              </div>
            )}

            <div className="summary-total">
              <span>Total</span>
              <strong>₹{totalAmount}</strong>
            </div>
          </div>

          <button
            className="place-order-btn"
            onClick={handlePlaceOrder}
            disabled={placingOrder || cartItems.length === 0 || loadingAddresses}
          >
            {placingOrder ? "Processing Payment..." : "Pay with Razorpay"}
          </button>
        </aside>
      </div>
    </div>
  );
}

export default Checkout;